import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setModalName, setModalVisible } from '@/lib/slices/modal-slice';
import { addToCourseList, setCourseList } from '@/lib/slices/course-slice';

import CourseGenModal from '@/common/components/modals/custom/content/CourseGenModal';
import { StepsIndicator } from '@/common/components/admin/modals/course-creator-steps';
import ModalComponent from '@/common/components/modals/ModalComponent';
import Button from '@/common/components/ui/Button';
import { AddIcon, AIIcon, SaveIcon } from '@/assets/icons/button';

import { CourseWizardStepRenderMap, StepMap } from '@/services/course/creation/course-step-map';
import UserCourseStorageService from '@/services/storage/user-course-storage-service';
import CourseCreationService from '@/services/course/creation/course-creation-service';
import CourseCreatorDraftStorageService from '@/services/storage/course-creator-draft-storage-service';
import { CourseBuilderData, CourseData, CourseEntity } from 'course-types';
import LoadingSpinner from '../../LoadingSpinner';

const FINAL_STEP_COUNT = Object.keys(StepMap).length;

function CourseCreatorWizard() {
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const modalVisible: boolean = useAppSelector(() => store.getState().modal.visible);
  const modalName: string = useAppSelector(() => store.getState().modal.name);

  const courseList: Array<CourseEntity> = useAppSelector(() => store.getState().course.courseList);
  const isEditingCourseData: boolean = useAppSelector(
    () => store.getState().course.isEditingCourseData,
  );

  const [courseData, setCourseData] = useState<CourseBuilderData>({
    name: '',
    image: null,
    units: [],
  });
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [completedSteps, setCompletedSteps] = useState<Array<boolean>>(
    new Array(FINAL_STEP_COUNT).fill(false),
  );
  const [confirmCloseModalOpen, setConfirmCloseModalOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Removed side-effect that mirrored Redux courseList into cookie.enterprise to avoid
  // accidental overwrites when navigating from group views.

  useEffect(() => {
    if (!modalVisible || modalName !== 'CreateCourse') {
      return;
    }

    if (courseData.name !== '' || courseData.units.length > 0) {
      return;
    }

    let cancelled = false;

    (async () => {
      const draft = await CourseCreatorDraftStorageService.getDraft();
      if (!draft || !draft.courseData || cancelled) {
        return;
      }

      setCourseData(draft.courseData);

      if (
        typeof draft.currentStep === 'number' &&
        draft.currentStep >= 0 &&
        draft.currentStep < FINAL_STEP_COUNT
      ) {
        setCurrentStep(draft.currentStep);
      }

      if (Array.isArray(draft.completedSteps) && draft.completedSteps.length === FINAL_STEP_COUNT) {
        setCompletedSteps(draft.completedSteps);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [modalVisible, modalName]);

  const updateCourseData = (
    field: string,
    value: any,
    unitIndex?: number,
    subunitIndex?: number,
  ) => {
    let newCourseData: CourseBuilderData = courseData;
    switch (field) {
      case 'name':
        if (typeof unitIndex !== 'number') {
          newCourseData = CourseCreationService.updateCourseName(newCourseData, value);
        } else if (typeof unitIndex === 'number' && typeof subunitIndex !== 'number') {
          newCourseData = CourseCreationService.updateUnit(newCourseData, unitIndex, field, value);
        } else if (typeof unitIndex === 'number' && typeof subunitIndex === 'number') {
          newCourseData = CourseCreationService.updateSubunit(
            newCourseData,
            unitIndex,
            subunitIndex,
            field,
            value,
          );
        }
        break;
      case 'description':
        if (typeof unitIndex === 'number' && typeof subunitIndex !== 'number') {
          newCourseData = CourseCreationService.updateUnit(newCourseData, unitIndex, field, value);
        } else if (typeof unitIndex === 'number' && typeof subunitIndex === 'number') {
          newCourseData = CourseCreationService.updateSubunit(
            newCourseData,
            unitIndex,
            subunitIndex,
            field,
            value,
          );
        }
        break;
      case 'image':
        newCourseData = CourseCreationService.updateCourseImage(newCourseData, value);
        break;
      default:
        break;
    }
    setCourseData(newCourseData);
  };

  // For traversing steps in the modal
  const handleNext = (stepIndex?: number) => {
    const newCompletedSteps = completedSteps;
    const nextIndexToComplete = completedSteps.findIndex((value) => value === false);

    // If step visited
    if (
      (typeof stepIndex === 'number' && completedSteps[stepIndex]) ||
      (stepIndex === nextIndexToComplete &&
        CourseCreationService.isNextButtonEnabled(courseData, stepIndex - 1))
    ) {
      setCurrentStep(stepIndex);
    }
    // If pressed next available
    else if (
      typeof stepIndex !== 'number' ||
      (stepIndex === currentStep + 1 &&
        CourseCreationService.isNextButtonEnabled(courseData, currentStep))
    ) {
      newCompletedSteps[currentStep] = true;
      setCurrentStep(currentStep + 1);
      setCompletedSteps(newCompletedSteps);
    }
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  // Course Generation Callback for setting course layout data
  const handleCourseGen = (data: CourseBuilderData) => {
    setCourseData(data);

    const newCompletedSteps = new Array(FINAL_STEP_COUNT).fill(true);
    newCompletedSteps[newCompletedSteps.length - 1] = false;
    setCompletedSteps(newCompletedSteps);

    setCurrentStep(newCompletedSteps.length - 1);
  };

  /* Handlers for course layout updates */
  const handleAddUnit = () => {
    const newCourseData = CourseCreationService.addUnit(courseData);
    setCourseData(newCourseData);
  };

  const handleAddSubunit = (unitIndex: number) => {
    const newCourseData = CourseCreationService.addSubunit(courseData, unitIndex);
    setCourseData(newCourseData);
  };

  const handleRemoveUnit = (index: number) => {
    const newCourseData = CourseCreationService.removeUnit(courseData, index);
    setCourseData(newCourseData);
  };

  const handleRemoveSubunit = (unitIndex: number, subunitIndex: number) => {
    const newCourseData = CourseCreationService.removeSubunit(courseData, unitIndex, subunitIndex);
    setCourseData(newCourseData);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      updateCourseData('image', e.target.files[0]);
    }
  };

  const handleSaveContent = (activityData: any, unitIndex: number, subunitIndex: number) => {
    const newCourseData = CourseCreationService.addActivity(
      courseData,
      unitIndex,
      subunitIndex,
      activityData,
    );
    setCourseData(newCourseData);
  };

  /* Handlers for submitting / closing course wizard */
  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    // Add to database
    const createResponse = await CourseCreationService.saveCourse(courseData);

    if (!createResponse || createResponse.error || createResponse.status == 'error') {
      toast.error('Failed to create Course');
    } else {
      toast.success('Course created successfully');
      // Wait for the dispatch to complete
      const createdCourseData: CourseData = createResponse.data;
      dispatch(addToCourseList({ id: createdCourseData.id, name: createdCourseData.name }));
      // Refresh cookie from server so enterprise list is canonical, then sync Redux
      await UserCourseStorageService.retrieveCourseCookie();
      const updatedCookie = UserCourseStorageService.getCourseCookie();
      if (updatedCookie) {
        dispatch(setCourseList(updatedCookie.all?.enterprise ?? []));
      }
    }

    handleClose();
  };

  const confirmClose = (): void => {
    if (courseData.name === '' && courseData.units.length === 0) {
      handleClose();
      return;
    }
    setConfirmCloseModalOpen(true);
  };

  const handleClose = (): void => {
    void CourseCreatorDraftStorageService.resetDraft();
    setCourseData({ name: '', image: null, units: [] });
    setCurrentStep(0);
    setCompletedSteps(new Array(FINAL_STEP_COUNT).fill(false));
    setConfirmCloseModalOpen(false);
    setIsSubmitting(false);

    dispatch(setModalVisible(false));
    dispatch(setModalName(''));
  };

  const saveDraft = (): void => {
    void CourseCreatorDraftStorageService.setDraft({
      courseData,
      currentStep,
      completedSteps,
    });
    dispatch(setModalVisible(false));
    dispatch(setModalName(''));
  };

  const openCourseAutoGen = (): void => {
    dispatch(setModalName('GenerateCourse'));
  };

  return (
    <>
      <ModalComponent
        isOpen={modalVisible && modalName == 'CreateCourse'}
        onRequestClose={confirmClose}
        contentLabel='Create Course'
        contentSubtitle='Build your course structure step by step'
        className='max-w-[900px] w-full'
        closeOnClickOff={false}
        footer={
          <div className='flex flex-row gap-8 py-2 border-t border-border_gray_300 pt-4 bg-surface_gray_50 sticky bottom-0'>
            {currentStep > 0 && (
              <Button
                outline={true}
                onClick={handlePrev}
                analyticsFeature='Generate Course'
                analyticsPage='Courses'
              >
                Previous
              </Button>
            )}
            {currentStep < FINAL_STEP_COUNT - 1 ? (
              <Button
                onClick={handleNext}
                disabled={!CourseCreationService.isNextButtonEnabled(courseData, currentStep)}
                className='ml-auto'
                analyticsFeature='Generate Course'
                analyticsPage='Courses'
              >
                Next Step
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={isEditingCourseData || isSubmitting}
                className='ml-auto'
                analyticsFeature='Generate Course'
                analyticsPage='Courses'
              >
                <AddIcon className='w-[24px] h-[24px] mr-2' /> Publish Course
              </Button>
            )}
          </div>
        }
      >
        <div className='relative w-full'>
          <div className='flex flex-col gap-2 w-full'>
            <div className='flex items-center gap-4 mb-4'>
              <Button
                outline={true}
                onClick={saveDraft}
                className='w-fit'
                analyticsFeature='Generate Course'
                analyticsPage='Courses'
              >
                <SaveIcon className='w-[20px] h-[20px] mr-2' /> Save Draft
              </Button>
              <Button
                onClick={openCourseAutoGen}
                className='ml-auto w-fit'
                analyticsFeature='Generate Course'
                analyticsPage='Courses'
              >
                <AIIcon className='w-[20px] h-[20px] mr-2' /> Generate Course
              </Button>
            </div>

            <StepsIndicator
              courseData={courseData}
              currentStep={currentStep}
              onClick={handleNext}
              completedSteps={completedSteps}
            />

            <div className='h-[50vh] min-h-[300px]'>
              {CourseWizardStepRenderMap({
                currentStep,
                courseData,
                handleChange: updateCourseData,
                handleAddUnit: handleAddUnit,
                handleAddSubunit: handleAddSubunit,
                handleRemoveUnit: handleRemoveUnit,
                handleRemoveSubunit: handleRemoveSubunit,
                handleSaveContent: handleSaveContent,
                handleImageChange: handleImageChange,
              })}
            </div>
          </div>

          {isSubmitting && (
            <div className='absolute top-0 left-0 z-10 w-full h-full flex items-center justify-center'>
              <div className='absolute inset-0 bg-surface_gray_50 opacity-70' />
              <div className='flex flex-col items-center justify-center gap-2 text-sm z-20'>
                <LoadingSpinner />
                <div>Publishing course...</div>
              </div>
            </div>
          )}
        </div>
      </ModalComponent>

      <CourseGenModal setCourseBuilderData={handleCourseGen} />

      <ModalComponent
        isOpen={confirmCloseModalOpen}
        onRequestClose={() => setConfirmCloseModalOpen(false)}
        contentLabel='Discard Changes?'
        contentSubtitle='Your changes will be lost'
        className='max-w-[500px] w-full'
        closeOnClickOff={true}
      >
        <div className='relative w-full'>
          <div className='flex flex-col gap-[16px] w-full'>
            <div className='flex flex-col gap-2 bg-surface_gray_200 rounded-lg p-4 border border-border_gray_300'>
              <p className='text-base'>Are you sure you want to discard your changes?</p>
              <p className='text-sm text-text_gray_900'>
                The course you&apos;re creating will not be saved.
              </p>
            </div>

            <div className='w-full pt-4 flex flex-col gap-2'>
              <div className='flex flex-row gap-8 py-2'>
                <Button
                  onClick={handleClose}
                  delete
                  analyticsFeature='Generate Course'
                  analyticsPage='Courses'
                >
                  Discard Changes
                </Button>
                <Button
                  outline
                  onClick={() => setConfirmCloseModalOpen(false)}
                  analyticsFeature='Generate Course'
                  analyticsPage='Courses'
                >
                  Continue Editing
                </Button>
              </div>
            </div>
          </div>
        </div>
      </ModalComponent>
    </>
  );
}

export default CourseCreatorWizard;
